import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import classnames from 'classnames';
import { useIntl } from 'react-intl';

import { ExpandableMessage } from 'ui-components/components/expandable-message';
import { TextType } from 'ui-components/components/text';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { useFulfillmentPlannerContext } from '../../../../../fulfillment-planner/context';

import style from './rate-warnings-and-errors.module.less';
import { getMessageDescriptorOrStringFromErrorString } from '../../../../../errors/user-friendly-error-message-service/error-string-matching-to-formatted-message';

const MessageType = {
  Warning: 'Warning',
  Error: 'Error',
};

const iconMap = {
  [MessageType.Warning]: {
    icon: faExclamationTriangle,
    iconClassName: style['is-warning'],
  },
  [MessageType.Error]: {
    icon: faExclamationCircle,
    iconClassName: style['is-error'],
  },
};

/**
 * @param {object} props
 * @param {boolean} [props.isDisabled] - Whether warnings/errors are disabled
 * @returns {JSX.Element | boolean}
 */
export const RateCardWarningsAndErrors = ({ isDisabled = false }) => {
  const { didLabelCreationFail, labelCreationErrors, rateErrors, warnings } = useFulfillmentPlannerContext();
  const intl = useIntl();

  const messageType = didLabelCreationFail ? MessageType.Error : MessageType.Warning;

  const { icon, iconClassName } = useMemo(() => {
    if (messageType === MessageType.Error) {
      return iconMap[MessageType.Error];
    }

    if (messageType === MessageType.Warning) {
      return iconMap[MessageType.Warning];
    }

    return emptyObject;
  }, [messageType]);

  const messagesToDisplay = useMemo(() => {
    const getMessages = () => {
      if (didLabelCreationFail) {
        return labelCreationErrors;
      }

      const hasRateErrors = !!rateErrors?.length;
      if (hasRateErrors) {
        return rateErrors.reduce((uniques, err) => {
          if (uniques.find((ue) => ue.defaultMessage === err.defaultMessage)) {
            return uniques;
          }

          return [...uniques, err];
        }, []);
      }

      const hasRateWarnings = !!warnings?.length;
      if (hasRateWarnings) {
        return warnings;
      }

      return emptyArray;
    };

    return getMessages().map((m) => {
      const message = getMessageDescriptorOrStringFromErrorString(m);
      if (typeof message === 'string') {
        return message;
      }
      return intl.formatMessage(message, message.values);
    });
  }, [didLabelCreationFail, intl, labelCreationErrors, rateErrors, warnings]);

  const shouldShowWarningsAndErrors = !isDisabled && messagesToDisplay?.length > 0;

  return (
    shouldShowWarningsAndErrors && (
      <div
        className={classnames(style.banner, {
          [style['is-warning']]: messageType === MessageType.Warning,
          [style['is-error']]: messageType === MessageType.Error,
        })}
      >
        <FontAwesomeIcon className={classnames(style.icon, iconClassName)} icon={icon} />
        <ExpandableMessage
          buttonClassName={style['banner-message-expander-button']}
          className={classnames(
            style['banner-message'],
            { [style['is-warning']]: messageType === MessageType.Warning },
            { [style['is-error']]: messageType === MessageType.Error },
          )}
          maxLines={2}
          messages={messagesToDisplay}
          textType={TextType.Caption}
        />
      </div>
    )
  );
};
